/*
  Reviews page (sections/pb-reviews-hero, -list, -products). Loaded with defer
  by those sections.

  Everything comes live from Reviews.io's public widget API, like the PDP v4
  reviews (assets/pdp-v4.js): store id only, no API key (theme code is
  public). Reviews.io has one store for every brand, so each part asks for the
  SKUs of its own collection, joined with ";" (data-sku, built in Liquid).

  - [data-pbrv-score]: average, count, distribution; [data-pbrv-count] anywhere
    in the section shows the count. Hidden if the API fails.
  - [data-pbrv-stats]: "★ 4,86 · 2.227 reviews" on a product card.
  - [data-pbrv-list]: product filter chips (with their counts), sort chips,
    paged list, "Toon meer reviews". Loads when it comes near the viewport.
    A chip with data-terms ("geur,ruikt") also sends terms=…: Reviews.io then
    only returns reviews whose text has one of those words (topic filters on
    the POWR ontdekfunnel LP).
*/
(function () {
  if (window.__pbReviews) return;
  window.__pbReviews = true;

  const API = 'https://api.reviews.io/timeline/data';
  const PAGE = 10;
  const cache = new Map();
  const numberFormat = new Intl.NumberFormat('nl-BE');
  const dateFormat = new Intl.DateTimeFormat('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' });
  const COUNTRIES = { BE: 'België', NL: 'Nederland', FR: 'Frankrijk', DE: 'Duitsland', LU: 'Luxemburg' };

  // One request per distinct query per page view; the first list page doubles as the stats call.
  function fetchReviews(store, sku, params) {
    const url = new URL(API);
    Object.entries({ type: 'product_review', store, sku, ...params }).forEach(([key, value]) =>
      url.searchParams.set(key, value),
    );
    // String, not URL: the fetch wrapper in layout/theme.liquid calls url.includes().
    const key = url.toString();
    if (!cache.has(key)) {
      const request = fetch(key).then((response) => {
        if (!response.ok) throw new Error(`Reviews.io ${response.status}`);
        return response.json();
      });
      request.catch(() => cache.delete(key));
      cache.set(key, request);
    }
    return cache.get(key);
  }

  const firstPage = (sort = 'date_desc') => ({ sort, page: 1, per_page: PAGE });
  const termsOf = (chip) => (chip.dataset.terms ? { terms: chip.dataset.terms } : {});
  const reviewCount = (data) => Number(data?.stats?.review_count) || 0;
  const formatRating = (value) => String(value).replace('.', ',');

  async function initScore(root) {
    const { store, sku } = root.dataset;
    try {
      const data = await fetchReviews(store, sku, firstPage());
      const count = reviewCount(data);
      if (!count) throw new Error('no reviews');
      root.querySelector('[data-pbrv-score-value]').textContent = formatRating(data.stats.average_rating);
      root
        .closest('section')
        ?.querySelectorAll('[data-pbrv-count]')
        .forEach((el) => {
          el.textContent = numberFormat.format(count);
        });
      const buckets = data.aggregations?.ratings?.buckets || [];
      root.querySelectorAll('[data-star]').forEach((bar) => {
        const docs = buckets.find((bucket) => String(bucket.key) === bar.dataset.star)?.doc_count || 0;
        const pct = (docs / count) * 100;
        bar.querySelector('[data-pbrv-fill]').style.width = `${pct}%`;
        // "<1%" instead of a rounded 0% next to a visible sliver.
        bar.querySelector('[data-pbrv-pct]').textContent = docs && pct < 1 ? '<1%' : `${Math.round(pct)}%`;
      });
      root.hidden = false;
    } catch (error) {
      root.hidden = true;
      window.PBDebug?.warn('[Reviews page] score niet geladen', error);
    }
  }

  async function initStats(el) {
    try {
      const data = await fetchReviews(el.dataset.store, el.dataset.sku, firstPage());
      const count = reviewCount(data);
      if (!count) return;
      el.textContent = `★ ${formatRating(data.stats.average_rating)} · ${numberFormat.format(count)} reviews`;
      el.hidden = false;
    } catch (error) {
      window.PBDebug?.warn('[Reviews page] productscore niet geladen', error);
    }
  }

  function place(address) {
    const first = String(address || '')
      .split(',')[0]
      .trim();
    return COUNTRIES[first.toUpperCase()] || first;
  }

  function initList(root) {
    const { store } = root.dataset;
    const list = root.querySelector('[data-pbrv-items]');
    const more = root.querySelector('[data-pbrv-more]');
    const template = root.querySelector('[data-pbrv-template]');
    const filters = [...root.querySelectorAll('[data-pbrv-filter]')];
    const sorts = [...root.querySelectorAll('[data-pbrv-sort]')];
    if (!list || !more || !template || !filters.length) return;

    let chip = filters[0];
    let sort = 'date_desc';
    let page = 0;
    // Bumped on every filter/sort change; a response from an older generation is dropped.
    let generation = 0;

    function renderReview(source) {
      // Reviews.io also stores star-only reviews; on this page they are an empty row.
      const text = String(source.comments || '').trim();
      if (!text) return;
      const item = template.content.firstElementChild.cloneNode(true);
      const rating = Number(source.rating) || 0;
      const stars = item.querySelector('[data-rv-stars]');
      stars.setAttribute('aria-label', `${rating} van 5 sterren`);
      stars.querySelectorAll('span').forEach((star, index) => star.classList.toggle('is-off', index >= rating));
      const date = new Date(String(source.date_created).replace(' ', 'T'));
      item.querySelector('[data-rv-date]').textContent = Number.isNaN(date.getTime()) ? '' : dateFormat.format(date);
      const tag = item.querySelector('[data-rv-tag]');
      const product = String(source.product_name || '')
        .replace(/^POWR\s+/i, '')
        .trim();
      if (product) tag.textContent = product;
      else tag.remove();
      // Review text comes from customers: textContent only, never innerHTML.
      item.querySelector('[data-rv-text]').textContent = `“${text}”`;
      const author = /^anonymous$/i.test(String(source.author || '').trim()) ? 'Anoniem' : source.author;
      item.querySelector('[data-rv-author]').textContent = [author, place(source.address)].filter(Boolean).join(' · ');
      list.append(item);
    }

    async function show(reset) {
      if (reset) {
        generation += 1;
        page = 0;
        list.replaceChildren();
      }
      const run = { generation, chip, sort, page };
      more.disabled = true;
      try {
        const data = await fetchReviews(store, run.chip.dataset.sku, {
          sort: run.sort,
          page: run.page + 1,
          per_page: PAGE,
          ...termsOf(run.chip),
        });
        if (run.generation !== generation) return;
        (data.timeline || []).forEach((entry) => renderReview(entry._source || {}));
        page = run.page + 1;
        more.hidden = page * PAGE >= (Number(data.stats?.results_count) || 0);
      } catch (error) {
        window.PBDebug?.warn('[Reviews page] reviews niet geladen', error);
      } finally {
        if (run.generation === generation) more.disabled = false;
      }
    }

    function press(group, chip) {
      group.forEach((other) => other.setAttribute('aria-pressed', String(other === chip)));
    }

    filters.forEach((filter) => {
      filter.addEventListener('click', () => {
        if (filter === chip) return;
        chip = filter;
        press(filters, filter);
        show(true);
      });
    });
    sorts.forEach((chip) => {
      chip.addEventListener('click', () => {
        if (chip.dataset.pbrvSort === sort) return;
        sort = chip.dataset.pbrvSort;
        press(sorts, chip);
        show(true);
      });
    });
    more.addEventListener('click', () => show(false));

    function start() {
      show(true);
      // Counts on the filter chips; the first page of each is cached for the click.
      filters.forEach(async (filter) => {
        try {
          const data = await fetchReviews(store, filter.dataset.sku, { ...firstPage(), ...termsOf(filter) });
          // With terms, review_count is still the whole SKU set; results_count is what matched.
          const count = filter.dataset.terms ? Number(data?.stats?.results_count) || 0 : reviewCount(data);
          const el = filter.querySelector('[data-pbrv-chip-count]');
          if (count && el) el.textContent = ` (${numberFormat.format(count)})`;
        } catch {
          /* chip stays without a count */
        }
      });
    }

    // Load right away, not on scroll: crawlers that render JS do not scroll, and the reviews are the page's SEO content.
    start();
  }

  function init(scope) {
    scope.querySelectorAll('[data-pbrv-score][data-sku]:not([data-pbrv-ready])').forEach((el) => {
      el.dataset.pbrvReady = '1';
      initScore(el);
    });
    scope.querySelectorAll('[data-pbrv-stats][data-sku]:not([data-pbrv-ready])').forEach((el) => {
      el.dataset.pbrvReady = '1';
      initStats(el);
    });
    scope.querySelectorAll('[data-pbrv-list]:not([data-pbrv-ready])').forEach((el) => {
      el.dataset.pbrvReady = '1';
      initList(el);
    });
  }

  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
